import { useEffect } from "react";
import { useInfiniteQuery, useQueryClient } from "@tanstack/react-query";
import { QueryKeys } from "../utils";
import { useAuth0 } from "@auth0/auth0-react";
import request from "@/lib/api/handler";

import { ConversationSummary } from "./user-conversation";

export interface ConversationMessage {
  id: string;
  clientId?: string;
  message: string;
  senderId?: string;
  senderName?: string;
  avatarLink?: string;
  createdAt: string;
  updatedAt: string;
  isOptimistic?: boolean;
}

const PAGE_SIZE = 20;

export const useChatMessageQuery = (conversationId: string) => {
  const qc = useQueryClient();
  const { getAccessTokenSilently } = useAuth0();

  const {
    isLoading,
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    error,
  } = useInfiniteQuery({
    queryKey: QueryKeys.chatMessage(conversationId),
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    enabled: !!conversationId,
    async queryFn({ pageParam = 0 }) {
      const token = await getAccessTokenSilently();
      return request<ConversationMessage[]>({
        method: "GET",
        path: `/api/chat/${conversationId}/messages`,
        params: { limit: PAGE_SIZE, offset: pageParam * PAGE_SIZE },
        token,
      });
    },
    getNextPageParam: (lastPage, allPages) =>
      lastPage.length < PAGE_SIZE ? undefined : allPages.length,
    initialPageParam: 0,
  });

  // Update lastMessage in ConversationSummary if the fetched message is newer
  useEffect(() => {
    const firstPage = data?.pages?.[0];
    if (!firstPage || firstPage.length === 0) {
      return;
    }

    const mostRecentMessage = firstPage[0]; // Most recent is first

    qc.setQueryData<ConversationSummary[]>(
      QueryKeys.chatConversations,
      (oldConversations) =>
        oldConversations?.map((conversation) =>
          conversation.id === conversationId &&
          (!conversation.lastMessage?.createdAt ||
            (mostRecentMessage &&
              mostRecentMessage.createdAt > conversation.lastMessage.createdAt))
            ? { ...conversation, lastMessage: mostRecentMessage }
            : conversation
        )
    );
  }, [data, conversationId, qc]);

  return {
    chatMessageLoading: isLoading,
    chatMessage: data ?? null,
    chatMessageError: error ?? null,
    getMoreMessages: fetchNextPage,
    hasMoreMessages: hasNextPage,
    gettingMoreMessages: isFetchingNextPage,
  };
};
